import { NavLink } from 'react-router-dom';
import {
  Banknote,
  Bell,
  Briefcase,
  CandlestickChart,
  ExternalLink,
  FileText,
  Gift,
  HelpCircle,
  LogOut,
  Moon,
  Plus,
  Repeat,
  Server,
  Sun,
  X,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useState } from 'react';
import { Logo, cn, useTheme } from '@pnl/ui';
import { api } from '../lib/api';
import { queryClient } from '../lib/queryClient';
import { useAuthStore } from '../auth/auth.store';

/**
 * Prefetch no hover: aquece o chunk JS lazy da rota + a query principal dela,
 * pra a aba abrir instantânea no clique. staleTime curto evita refetch redundante.
 */
const ROUTE_PREFETCH: Record<string, () => void> = {
  '/accounts': () => {
    void import('../pages/AccountsPage');
    void queryClient.prefetchQuery({
      queryKey: ['trader-accounts'],
      queryFn: () => api.getTraderAccounts(),
    });
  },
  '/withdrawals': () => {
    void import('../pages/WithdrawalsPage');
    void queryClient.prefetchQuery({
      queryKey: ['trader-withdrawals'],
      queryFn: () => api.getTraderWithdrawals(),
    });
  },
  '/replicator': () => void import('../pages/ReplicatorPage'),
  '/creditos': () => {
    void import('../pages/CreditsPage');
    void queryClient.prefetchQuery({
      queryKey: ['trader-credits'],
      queryFn: () => api.getTraderCredits(),
    });
  },
  '/afiliado': () => {
    void import('../pages/AffiliatePage');
    void queryClient.prefetchQuery({
      queryKey: ['affiliate-me'],
      queryFn: () => api.getAffiliateMe(),
    });
  },
  '/platforms': () => {
    void import('../pages/PlatformsPage');
    void queryClient.prefetchQuery({
      queryKey: ['trader-platforms'],
      queryFn: () => api.getTraderPlatforms(),
    });
  },
  '/documents': () => void import('../pages/DocumentsPage'),
  '/notifications': () => {
    void import('../pages/NotificationsPage');
    void queryClient.prefetchQuery({
      queryKey: ['trader-notifications'],
      queryFn: () => api.getTraderNotifications(),
    });
  },
  '/support': () => void import('../pages/SupportPage'),
};

function prefetchRoute(to: string): void {
  ROUTE_PREFETCH[to]?.();
}

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  /** Item que dispara ação (ex: handoff BlackArrow) em vez de navegar. */
  action?: 'blackarrow';
}

const sections: { label: string; items: NavItem[] }[] = [
  {
    label: 'Operação',
    items: [
      { to: '/dashboard', label: 'Dashboard B3', icon: CandlestickChart },
      // Dashboard Forex agora é INTERNO (era um link pra plataforma externa).
      // Mesma tela do B3; os dados próprios entram com a API da Nelogica.
      {
        to: '/dashboard-forex',
        label: 'Dashboard Forex/Futuros',
        icon: CandlestickChart,
      },
      { to: '/accounts', label: 'Minhas contas', icon: Briefcase },
    ],
  },
  {
    label: 'Financeiro',
    items: [
      { to: '/withdrawals', label: 'Saques', icon: Banknote },
      { to: '/replicator', label: 'Replicador', icon: Repeat },
      { to: '/creditos', label: 'Indicações', icon: Gift },
    ],
  },
  {
    label: 'Conta',
    items: [
      { to: '/platforms', label: 'Plataformas', icon: Server },
      { to: '/documents', label: 'Documentos', icon: FileText },
      { to: '/notifications', label: 'Notificações', icon: Bell },
      { to: '/support', label: 'Suporte', icon: HelpCircle },
    ],
  },
];

/** O Programa de Afiliados agora vive DENTRO da aba Indicações (pedido do
 * cliente: os dois programas na mesma aba). Mantemos a rota /afiliado viva
 * pra links antigos, mas sem item proprio no menu. */

const activeCls = 'bg-brand-500 shadow-soft-dark text-white';
const inactiveCls = 'text-ink-200 hover:bg-white/5 hover:text-white';
const baseItemCls =
  'flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors';

/**
 * Toggle claro/escuro do rodapé da sidebar. Alterna APENAS light/dark (sem
 * 'system' — evita estado ambíguo). O conteúdo do app inteiro reage via as
 * CSS vars da escala ink; sidebar/topbar ficam escuras sempre (bg-brand-900
 * é hex fixo). A sidebar é sempre escura, então as cores do item são fixas.
 */
function ThemeToggleItem() {
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(baseItemCls, 'text-ink-200 hover:bg-white/5 hover:text-white')}
      aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
      {isDark ? 'Modo claro' : 'Modo escuro'}
    </button>
  );
}

/** Conteúdo compartilhado entre a sidebar desktop e o drawer mobile. */
function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const logout = useAuthStore((s) => s.logout);
  const [handoffLoading, setHandoffLoading] = useState(false);

  // Sem itens condicionais por role: o Programa de Afiliados passou a viver
  // dentro da aba Indicações (a própria página mostra a seção só pra quem é
  // afiliado), então o menu é igual pra todo mundo.
  const navSections = sections;

  async function openBlackArrow() {
    if (handoffLoading) return;
    setHandoffLoading(true);
    try {
      const { url } = await api.getBlackArrowHandoff();
      window.open(url, '_blank', 'noopener,noreferrer');
      onNavigate?.();
    } catch (err) {
      console.error('blackarrow handoff failed', err);
    } finally {
      setHandoffLoading(false);
    }
  }

  return (
    <>
      <div className="flex h-20 items-center border-b border-white/5 px-6 pt-1">
        <Logo tone="dark" />
      </div>

      {/* CTA destaque "Novo Challenge" (estilo FTMO) */}
      <div className="px-4 pt-5">
        <a
          href="https://pnlglobalb3.com.br/planos"
          target="_blank"
          rel="noreferrer"
          className="bg-brand-500 hover:bg-brand-600 shadow-soft-dark flex w-full items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-white transition-colors"
        >
          <Plus size={16} />
          Novo Challenge
        </a>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {navSections.map((section) => (
          <div key={section.label} className="mb-6">
            <p className="text-2xs text-ink-400 px-3 pb-1.5 font-semibold uppercase tracking-widest">
              {section.label}
            </p>
            <ul className="space-y-1.5">
              {section.items.map((item) => {
                const Icon = item.icon;

                if (item.action === 'blackarrow') {
                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        onClick={() => void openBlackArrow()}
                        disabled={handoffLoading}
                        className={cn(baseItemCls, inactiveCls, 'disabled:opacity-60')}
                        title="Abre a plataforma BlackArrow em nova aba"
                      >
                        <Icon size={16} />
                        <span className="flex-1 text-left">{item.label}</span>
                        <ExternalLink size={12} className="opacity-70" />
                      </button>
                    </li>
                  );
                }

                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={() => onNavigate?.()}
                      onMouseEnter={() => prefetchRoute(item.to)}
                      onFocus={() => prefetchRoute(item.to)}
                      className={({ isActive }) =>
                        cn(baseItemCls, isActive ? activeCls : inactiveCls)
                      }
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="space-y-1 border-t border-white/5 px-3 py-3">
        <ThemeToggleItem />
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            void logout();
          }}
          className={cn(baseItemCls, 'text-ink-200 hover:text-danger-300 hover:bg-white/5')}
        >
          <LogOut size={16} />
          Sair
        </button>
        <p className="text-2xs text-ink-500 px-3 pt-2">v0.5 · Marco 4</p>
      </div>
    </>
  );
}

/** Sidebar fixa (desktop ≥ lg). */
export function Sidebar() {
  return (
    <aside className="force-dark bg-brand-900 text-ink-200 sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-white/5 lg:flex">
      <SidebarBody />
    </aside>
  );
}

/** Drawer mobile (< lg) com overlay. Controlado pelo TraderLayout. */
export function MobileSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <div className={cn('fixed inset-0 z-40 lg:hidden', open ? '' : 'pointer-events-none')}>
      <div
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      />
      <aside
        className={cn(
          'force-dark bg-brand-900 text-ink-200 absolute left-0 top-0 flex h-full w-64 flex-col border-r border-white/5 transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className="text-ink-300 absolute right-3 top-4 z-10 rounded-md p-1.5 hover:bg-white/10 hover:text-white"
          aria-label="Fechar menu"
        >
          <X size={18} />
        </button>
        <SidebarBody onNavigate={onClose} />
      </aside>
    </div>
  );
}
